import React, { createContext, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSelector } from 'react-redux';

const giftFormSchema = yup
  .object({
    delivery: yup
      .string()
      .oneOf(['email', 'link'], 'Delivery method must be email or link')
      .required('Delivery method is required'),
    isUsingUSDC: yup.boolean(),
    userEmail: yup.string(),
    email: yup.string().when('delivery', {
      is: 'email',
      then: (schema) =>
        schema
          .email('Must be a valid email')
          .required('Email is required')
          .test(
            'email-match',
            'You cannot use your own email',
            (value, context) => value !== context.parent.userEmail,
          ),
      otherwise: (schema) => schema.optional(),
    }),
    message: yup
      .string()
      .optional()
      .max(190, 'Message must be at most 190 characters'),
    senderName: yup
      .string()
      .optional()
      .max(32, 'From field must be at most 40 characters'),
    image: yup.string().required('Card image is required'),
    network: yup.string().required('Network is required'),
    targetTokenAddress: yup.string().required('Token address is required'),
    recipientName: yup.string(),
    addressLine1: yup.string(),
    addressLine2: yup.string(),
    city: yup.string(),
    formattedAddress: yup.string(),
    state: yup.string(),
    postalCode: yup.string(),
    country: yup.string(),
    usdAmount: yup.string().when('isUsingUSDC', {
      is: true,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Minimum amount is 1', (value) => {
            const n = Number(value);
            return !isNaN(n) && n >= 1;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    targetTokenAmount: yup.string().when('isUsingUSDC', {
      is: false,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Must be greater than 0', (value) => {
            const n = Number(value);
            return !isNaN(n) && n > 0;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    shippingAddressId: yup.number().when('delivery', {
      is: 'mail',
      then: (schema) => schema.required('Shipping address is required'),
      otherwise: (schema) => schema.optional(),
    }),
  })
  .required();

const GiftFormContext = createContext(null);

const defaultInitialValues = {
  isUsingUSDC: true,
  delivery: 'link',
  usdAmount: '25',
  targetTokenAmount: '',
};

export const GiftFormProvider = ({
  children,
  schema = giftFormSchema,
  mode = 'all',
}) => {
  const form = useForm({
    resolver: yupResolver(schema),
    mode,
    defaultValues: {
      ...defaultInitialValues,
    },
  });

  const privyToken = useSelector(selectPrivyToken);
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const userEmail = currentUser?.email;

  const {
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    control,
  } = form;

  const [selectedPaymentToken, setSelectedPaymentToken] = useState(null);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    useState(null); // 'token', 'applePay'
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [claimUrl, setClaimUrl] = useState(null);
  const [fromUsdcDirection, setFromUsdcDirection] = useState(true);
  const [gift, setGift] = useState(null);
  const [payingWith, setPayingWith] = useState(null);
  const isUsingUSDC = watch('isUsingUSDC');
  const delivery = watch('delivery');
  const email = watch('email');
  const message = watch('message');
  const senderName = watch('senderName');
  const recipientName = watch('recipientName');
  const image = watch('image');
  const targetTokenAddress = watch('targetTokenAddress');
  const network = watch('network');
  const shippingAddressId = watch('shippingAddressId');
  const usdAmount = watch('usdAmount');
  const targetTokenAmount = watch('targetTokenAmount');
  const addressLine1 = watch('addressLine1');
  const addressLine2 = watch('addressLine2');
  const city = watch('city');
  const state = watch('state');
  const postalCode = watch('postalCode');
  const country = watch('country');
  const formattedAddress = watch('formattedAddress');

  useEffect(() => {
    if (userEmail && setValue) {
      setValue('userEmail', userEmail);
    }
  }, [userEmail, setValue]);

  useEffect(() => {
    // Clear errors when delivery method changes away from requiring those fields
    // Don't trigger validation immediately - let it happen on user interaction
    if (delivery !== 'email') {
      clearErrors('email');
    }
    if (delivery !== 'mail') {
      clearErrors('shippingAddressId');
    }
  }, [delivery, clearErrors]);

  useEffect(() => {
    trigger('shippingAddressId');
  }, [shippingAddressId]);

  const initializeForm = ({ initialValues }) => {
    setSelectedPaymentToken(null);
    setSelectedPaymentMethodType(null);
    setClaimUrl(null);
    setSendingStatus('idle');
    setFeedback(null);
    setFromUsdcDirection(true);
    form.reset({
      ...defaultInitialValues,
      ...initialValues,
      userEmail,
    });
    clearErrors();
  };

  return (
    <GiftFormContext.Provider
      value={{
        handleSubmit,
        control,
        errors,
        isValid,
        isSubmitting,
        setValue,
        claimUrl,
        gift,
        setGift,
        setFeedback,
        setClaimUrl,
        setSendingStatus,
        clearErrors,
        trigger,
        payingWith,
        setPayingWith,
        isUsingUSDC,
        delivery,
        email,
        message,
        image,
        network,
        sendingStatus,
        feedback,
        usdAmount,
        selectedPaymentToken,
        setSelectedPaymentToken,
        selectedPaymentMethodType,
        setSelectedPaymentMethodType,
        targetTokenAddress,
        targetTokenAmount,
        initializeForm,
        setIsUsingUSDC: (newValue) => setValue('isUsingUSDC', newValue),
        fromUsdcDirection,
        senderName,
        setFromUsdcDirection,
        addressLine1,
        addressLine2,
        city,
        recipientName,
        state,
        postalCode,
        country,
        formattedAddress,
      }}
    >
      {children}
    </GiftFormContext.Provider>
  );
};

export const useGiftForm = () => {
  const ctx = useContext(GiftFormContext);
  if (!ctx)
    throw new Error('useGiftForm must be used within a GiftFormProvider');
  return ctx;
};

export { giftFormSchema };
